const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://photo-x-yash-bhai.onrender.com/api/v1"
    : "http://localhost:4100/api/v1";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new ApiError(
      payload?.error?.message ||
        `Request failed with status ${response.status}`,
      response.status,
      payload?.error?.code,
    );
  }
  return payload;
}

export async function requestOtp(email) {
  const payload = await request("/auth/otp/request", {
    method: "POST",
    body: {
      email,
      audience: "user",
      ...(globalThis.location?.href
        ? { returnUrl: globalThis.location.href }
        : {}),
    },
  });
  return payload.data;
}

export async function verifyOtp(email, code) {
  const payload = await request("/auth/otp/verify", {
    method: "POST",
    body: { email, code, audience: "user", intent: "client" },
  });
  return payload.data;
}

export async function exchangeAuthHandoff(token) {
  const payload = await request("/auth/handoff/exchange", {
    method: "POST",
    body: { token, workspace: "client" },
  });
  return payload.data;
}

export async function signOut() {
  return request("/auth/logout", { method: "POST" });
}

export async function loadWorkspace(signal) {
  const payload = await request("/client/workspace", { signal });
  return payload.data;
}

export async function updateClientIntake(values) {
  const payload = await request("/client/intake", {
    method: "PUT",
    body: values,
  });
  return payload.data.user;
}

export async function updateConsent(privacyVersion, accepted) {
  const payload = await request("/account/consent", {
    method: "PUT",
    body: { privacyVersion, accepted },
  });
  return payload.data.user;
}

export async function loadSupport() {
  const payload = await request("/client/support");
  return payload.data.tickets;
}

export async function createSupport(values) {
  const payload = await request("/client/support", {
    method: "POST",
    body: values,
  });
  return payload.data.ticket;
}

export async function downloadPhoto(eventId, photoId) {
  const payload = await request(
    `/events/${eventId}/photos/${photoId}/media?variant=original`,
  );
  return payload.data.delivery;
}

// The API paginates event galleries. Limit signed-URL requests so thousands of
// photos never create an unbounded burst of requests.
export async function loadGalleryPage(
  eventId,
  scope = "visible",
  page = 1,
  signal,
  limit = 24,
) {
  const safePage = Number.isInteger(page) && page > 0 ? page : 1;
  const size = Math.max(1, Math.min(Number(limit) || 24, 50));
  const gallery = await request(
    `/events/${encodeURIComponent(eventId)}/gallery?scope=${encodeURIComponent(scope)}&page=${safePage}&limit=${size}`,
    { signal },
  );
  const photos = Array.isArray(gallery.data) ? gallery.data : [];
  const result = photos.map((photo) => ({ ...photo, mediaUrl: null }));
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, result.length) }, async () => {
      while (cursor < result.length) {
        if (signal?.aborted) return;
        const photo = result[cursor++];
        if (
          !photo.preview?.available &&
          !photo.thumbnail?.available &&
          !photo.original?.available
        )
          continue;
        const variant = photo.thumbnail?.available
          ? "thumbnail"
          : photo.preview?.available
            ? "preview"
            : "original";
        try {
          const media = await request(
            `/events/${encodeURIComponent(eventId)}/photos/${encodeURIComponent(photo.id)}/media?variant=${variant}`,
            { signal },
          );
          photo.mediaUrl = media.data?.delivery?.url || null;
        } catch (error) {
          if (error.name === "AbortError") throw error;
        }
      }
    }),
  );
  const pagination = gallery.meta || {};
  return {
    photos: result,
    pagination: {
      page: safePage,
      limit: size,
      total: pagination.total ?? result.length,
      pages:
        pagination.pages ?? (result.length < size ? safePage : safePage + 1),
    },
  };
}

// Backward-compatible API for callers that expect a simple array.
export async function loadGallery(eventId, scope = "visible", signal) {
  return (await loadGalleryPage(eventId, scope, 1, signal)).photos;
}

export async function setFavorite(eventId, photoId, favorite) {
  return request(`/events/${eventId}/favorites/${photoId}`, {
    method: favorite ? "PUT" : "DELETE",
  });
}

export async function setSharing(eventId, photoId, shared) {
  return request(
    `/events/${eventId}/photos/${photoId}/${shared ? "share" : "unshare"}`,
    { method: "POST" },
  );
}

export async function loadPlans(signal) {
  const payload = await request("/billing/plans", { signal });
  return payload.data.plans;
}

export async function loadEventSubscription(eventId, signal) {
  const payload = await request(`/events/${eventId}/subscription`, { signal });
  return payload.data.subscription;
}

export async function startEventCheckout(eventId, planCode) {
  const payload = await request(`/events/${eventId}/subscription/checkout`, {
    method: "POST",
    body: { planCode },
  });
  return payload.data.checkout;
}

export async function loadEventReceipts(eventId) {
  const payload = await request(`/events/${eventId}/subscription/receipts`);
  return payload.data.receipts;
}

export async function createEventPortal(eventId) {
  const payload = await request(`/events/${eventId}/subscription/portal`, {
    method: "POST",
  });
  return payload.data.portal;
}

export async function createEvent(values) {
  const payload = await request("/events", { method: "POST", body: values });
  return payload.data.event;
}

export async function rotateEventAccess(eventId) {
  const payload = await request(`/events/${eventId}/access/rotate`, {
    method: "POST",
    body: {},
  });
  return payload.data.access;
}

export async function uploadPhoto(eventId, file, kind) {
  const initiated = await request(`/events/${eventId}/uploads`, {
    method: "POST",
    body: {
      kind,
      originalFilename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      mode: "single",
    },
  });
  const upload = initiated.data.upload;
  const uploadResponse = await fetch(upload.url, {
    method: upload.method || "PUT",
    headers: upload.headers || { "Content-Type": file.type },
    body: file,
  });
  if (!uploadResponse.ok)
    throw new ApiError(
      "Object storage upload failed",
      uploadResponse.status,
      "STORAGE_UPLOAD_FAILED",
    );
  await request(
    `/events/${eventId}/uploads/${initiated.data.photo.id}/complete`,
    { method: "POST", body: {} },
  );
  return initiated.data.photo;
}

export async function faceSearch(eventId, selfieBase64) {
  const payload = await request(`/events/${eventId}/face-search`, {
    method: "POST",
    body: { selfieBase64, consentConfirmed: true },
  });
  return payload.data.matches;
}

export { API_BASE_URL };

export async function loadPublicNavigation() {
  const result = await request("/public/navigation");
  return result.data;
}
